import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface KpiTarget {
  id: string;
  kpi_code: string;
  kpi_name: string;
  kpi_category: string | null;
  target_value: number;
  target_type: 'min' | 'max' | 'exact' | 'range' | null;
  target_min: number | null;
  target_max: number | null;
  unit: string | null;
  year: number;
  month: number | null;
  country: string | null;
  owner: string | null;
  is_active: boolean;
}

export interface KpiStatus {
  kpi: KpiTarget;
  currentValue: number;
  status: 'ok' | 'warning' | 'critical';
  percentageVsTarget: number;
  gap: number;
}

export function useKpiTargets(year: number = new Date().getFullYear()) {
  return useQuery({
    queryKey: ['kpi-targets', year],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kpi_targets')
        .select('*')
        .eq('year', year)
        .eq('is_active', true)
        .order('kpi_category', { ascending: true });

      if (error) throw error;
      return data as KpiTarget[];
    },
  });
}

/**
 * Hook que calcula os KPIs com valores reais
 * Busca os targets e os valores atuais do banco
 */
export function useKpiWithStatus(year: number = new Date().getFullYear()) {
  return useQuery({
    queryKey: ['kpi-status', year],
    queryFn: async () => {
      // 1. Buscar targets
      const { data: targets, error: targetsError } = await supabase
        .from('kpi_targets')
        .select('*')
        .eq('year', year)
        .eq('is_active', true);

      if (targetsError) throw targetsError;

      // 2. Buscar valores reais para cada KPI
      const kpiStatuses: KpiStatus[] = [];
      const currentMonth = new Date().getMonth() + 1;

      for (const target of targets || []) {
        let currentValue = 0;

        switch (target.kpi_code) {
          case 'revenue': {
            // Receita do mês atual via v_pdd_total
            const { data } = await supabase
              .from('v_pdd_total')
              .select('total_a_receber');
            currentValue = data?.reduce((sum, row) => sum + (row.total_a_receber || 0), 0) || 0;
            break;
          }
          
          case 'delinquency': {
            // Inadimplência via v_payments_aging
            const { data: aging } = await supabase
              .from('v_payments_aging')
              .select('faixa, valor_aberto');
            
            const total = aging?.reduce((sum, row) => sum + (row.valor_aberto || 0), 0) || 0;
            const overdue = aging
              ?.filter(row => row.faixa !== '0_a_vencer')
              .reduce((sum, row) => sum + (row.valor_aberto || 0), 0) || 0;
            
            currentValue = total > 0 ? (overdue / total) * 100 : 0;
            break;
          }
          
          case 'leads': {
            // DEPRECATED - leads table removed; count deals instead
            const startOfMonth = new Date(year, currentMonth - 1, 1).toISOString();
            const endOfMonth = new Date(year, currentMonth, 0).toISOString();
            
            const { count } = await supabase
              .from('deals')
              .select('*', { count: 'exact', head: true })
              .gte('created_at', startOfMonth)
              .lte('created_at', endOfMonth);
            
            currentValue = count || 0;
            break;
          }
          
          case 'conversion': {
            // Taxa de conversão (deals WON / total deals)
            const { count: wonDeals } = await supabase
              .from('deals')
              .select('*', { count: 'exact', head: true })
              .eq('stage', 'won');
            
            const { count: totalDeals } = await supabase
              .from('deals')
              .select('*', { count: 'exact', head: true });
            
            currentValue = totalDeals && totalDeals > 0 
              ? ((wonDeals || 0) / totalDeals) * 100 
              : 0;
            break;
          }
          
          case 'cash_runway': {
            // Placeholder - requer saldo de caixa e despesas médias
            currentValue = 4; // Meses de runway estimado
            break;
          }
          
          case 'margin': {
            // Placeholder - requer DRE completa
            currentValue = 55; // Margem estimada
            break;
          }
          
          case 'cac': {
            // Placeholder - requer cálculo de CAC
            currentValue = 2200;
            break;
          }
          
          case 'nps': {
            // Placeholder - requer pesquisa NPS
            currentValue = 72;
            break;
          }
          
          default:
            currentValue = 0;
        }

        // Calcular status
        let status: 'ok' | 'warning' | 'critical' = 'ok';
        let gap = 0;
        let percentageVsTarget = 0;

        if (target.target_type === 'min') {
          percentageVsTarget = (currentValue / target.target_value) * 100;
          gap = currentValue - target.target_value;
          
          if (currentValue >= target.target_value) {
            status = 'ok';
          } else if (currentValue >= target.target_value * 0.9) {
            status = 'warning';
          } else {
            status = 'critical';
          }
        } else if (target.target_type === 'max') {
          percentageVsTarget = (target.target_value / currentValue) * 100;
          gap = target.target_value - currentValue;
          
          if (currentValue <= target.target_value) {
            status = 'ok';
          } else if (currentValue <= target.target_value * 1.1) {
            status = 'warning';
          } else {
            status = 'critical';
          }
        }

        kpiStatuses.push({
          kpi: target as KpiTarget,
          currentValue,
          status,
          percentageVsTarget,
          gap,
        });
      }

      return kpiStatuses;
    },
    staleTime: 60000, // Cache por 1 minuto
  });
}
